import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { readGlobalProfiles, updateGlobalProfile } from '@services/firebaseGlobal';
import { getFirebase, getProdDb } from '@services/firebase';
import { FN_BASE } from '@services/fnBase';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { FaTimes, FaArrowRight } from 'react-icons/fa';

const Login = () => {
  const { auth } = getFirebase();
  const [currentUser] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState(location.state?.email || '');
  const [securityCode, setSecurityCode] = useState('');
  const [referenceCode, setReferenceCode] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [isPreApprovedLogin, setIsPreApprovedLogin] = useState(false);

  const [error, setError] = useState(
    location.state?.fromSignup ? 'Successfully Signed Up' : 'Enter your credential to login to login'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingProfile, setIsCheckingProfile] = useState(false);

  // Auto-trigger security code send if navigated from Signup with an existing email
  useEffect(() => {
    if (location.state?.autoSend && location.state?.email) {
      sendSecurityCode();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry mechanism for getDoc
  const getDocWithRetry = async (docRef, maxRetries = 3, delayMs = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          return docSnap;
        }
        if (attempt === maxRetries) {
          return docSnap;
        }
        await new Promise(resolve => setTimeout(resolve, delayMs));
      } catch (err) {
        if (attempt === maxRetries) {
          throw err;
        }
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  };

  // Retry mechanism for updateGlobalProfile
  const updateGlobalProfileWithRetry = async (collection, id, data, maxRetries = 3, delayMs = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await updateGlobalProfile(collection, id, data);
        return;
      } catch (err) {
        if (attempt === maxRetries) {
          throw err;
        }
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  };

  const sendSecurityCode = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (normalizedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError('Please enter a valid email.');
      return;
    } else if (!normalizedEmail) {
      setError('Enter your credential to login');
      return;
    }

    // Show spinner immediately — before any async work
    setIsCheckingProfile(true);
    setError('Searching for valid profile...');

    try {
      const profileRecords = await readGlobalProfiles('profile');
      const profileDoc = profileRecords.find(doc => doc.email?.trim().toLowerCase() === normalizedEmail);
      if (!profileDoc) {
        navigate('/signup', { state: { email: normalizedEmail } });
        return;
      }

      const cUsersSnap = await getDoc(doc(getProdDb(), 'cUsers', profileDoc.id));
      if (!cUsersSnap.exists()) {
        setError('No User Data Found');
        return;
      }

      if (cUsersSnap.data().first_login) {
        const pendingSnap = await getDoc(doc(getProdDb(), 'pendingSecurityCodes', normalizedEmail));
        if (!pendingSnap.exists()) {
          setError('No Reference Found');
          return;
        }
        if (!pendingSnap.data().isEmailVerified) {
          setError('Email NOT Verified! Please Confirm Your Email');
          return;
        }
        const preApproved = pendingSnap.data().isPreApproved === true;
        setIsPreApprovedLogin(preApproved);
        if (preApproved && pendingSnap.data().securityCode) setReferenceCode(String(pendingSnap.data().securityCode));
      }

      // Profile & user checks passed — now send the security code
      const response = await fetch(`${FN_BASE}/sendSecurityCode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error?.message);
      }
      setLoginPass(data.loginPass);
      setError('Security code sent. Please check your email.');
      setIsFirstLogin(cUsersSnap.data().first_login || false);
    } catch (err) {
      setError(`Failed to send security code: ${err.message}`);
    } finally {
      // Spinner off only when the entire process (including code send) is complete
      setIsCheckingProfile(false);
    }
  };

  const navigatingToSignup = useRef(false);

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const handleEmailBlur = () => {
    if (navigatingToSignup.current) return;
    const normalized = email.trim().toLowerCase();
    if (normalized && EMAIL_REGEX.test(normalized)) {
      sendSecurityCode();
    }
  };

const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Fetch profile to get UID based on email
      const normalizedEmail = email.trim().toLowerCase();
      const profileRecords = await readGlobalProfiles('profile');
      const profileDoc = profileRecords.find(doc => doc.email?.trim().toLowerCase() === normalizedEmail);
      if (!profileDoc) {
        throw new Error('User not found with the provided email.');
      }
      
      // Fetch cUsers to validate security code
      const cUsersRecords = await readGlobalProfiles('cUsers');
      const userDoc = cUsersRecords.find(doc => doc.id === profileDoc.id && (doc.isflag === true || doc.first_login === true));
      if (!userDoc) {
        throw new Error('User not found or profile invalid in cUsers.');
      }

      const userDocSnap = await getDocWithRetry(doc(getProdDb(), 'cUsers', userDoc.id));
      if (!userDocSnap.exists()) {
        throw new Error('User document not found in cUsers.');
      }

      const userDocData = { id: userDocSnap.id, ...userDocSnap.data() };
      const storedSecurityCode = userDocData.securityCode;

      if (userDocData.first_login) {
        const pendingSnap = await getDocWithRetry(doc(getProdDb(), 'pendingSecurityCodes', email));
        if (!pendingSnap.exists()) {
          setError('Reference code record not found. Please contact an administrator.');
          return;
        }
        const storedReferenceCode = pendingSnap.data().securityCode;
        if (!referenceCode || referenceCode !== storedReferenceCode) {
          setError('Invalid reference code. Please try again.');
          return;
        }
      }

      if (!securityCode || securityCode !== storedSecurityCode) {
        setError('Invalid security code. Please try again.');
        return;
      }

      // Proceed with authentication only if security code matches
      await signInWithEmailAndPassword(auth, email, loginPass);
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Authentication failed.');
      }

      // Get fresh ID token after authentication
      const token = await user.getIdToken();

      // Fetch admin UIDs from the getAdminUids Cloud Function with retry and detailed logging
      let adminUids = [];
      const fetchAdminUids = async (retries = 3) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            const response = await fetch(`${FN_BASE}/getAdminUids`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            });
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            if (data.success) {
              adminUids = data.uids || [];
              return;
            } else {
              throw new Error(data.error || 'Failed to fetch admin UIDs');
            }
          } catch (err) {
            console.error(`Attempt ${attempt} - Error fetching admin UIDs from getAdminUids:`, err.message);
            if (attempt === retries) {
              adminUids = []; // Fallback to empty array after all retries
              console.error('Max retries reached, using empty admin UIDs list');
            } else {
              await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); 
            }
          }
        }
      };
      await fetchAdminUids();

      // Check if user is in adminUids list
      const isAdmin = adminUids.includes(user.uid);
      let login_state = '';
      // Flag updates after successful authentication
      if (userDocData.first_login) {
        login_state = 'first';
        await updateGlobalProfileWithRetry('cUsers', user.uid, {
          first_login: false,
          isflag: true,
          last_login: new Date().toISOString(),
          securityCode: null,
          lastSecurityCodeSent: null,
        });
      }

      if (isAdmin && !userDocData.isadminuser) {
        login_state = 'msdcadmin';
        await updateGlobalProfileWithRetry('cUsers', user.uid, {
          isadminuser: true,
          issuperuser: false,
          isuser: false,
          ismover: false,
          isbuyer: false,
          isactive: true,
          last_login: new Date().toISOString(),
          securityCode: null,
          lastSecurityCodeSent: null,
        });
      } else if (!isAdmin && userDocData.isadminuser) {
        login_state = 'msadmin';
        await updateGlobalProfileWithRetry('cUsers', user.uid, {
          isadminuser: false,
          issuperuser: userDocData.issuperuser || false,
          isuser: true,
          ismover: userDocData.ismover || false,
          isbuyer: userDocData.isbuyer || false,
          isactive: true,
          last_login: new Date().toISOString(),
          securityCode: null,
          lastSecurityCodeSent: null,
        });
      } else {
        login_state = 'normal';
        await updateGlobalProfileWithRetry('cUsers', user.uid, {
          isactive: true,
          last_login: new Date().toISOString(),
          securityCode: null,
          lastSecurityCodeSent: null,
        });
      }

      navigate('/');
    } catch (err) {
      setError(`Failed to log in: ${err.message}`);
      console.error('Error logging in:', err.message, err.stack);
      if (auth.currentUser) 
      {
        if (login_state == 'first') 
          {
            await updateGlobalProfileWithRetry('cUsers', user.uid, {first_login: true,isflag: false,});
          }
        await updateGlobalProfileWithRetry('cUsers', user.uid, {isactive: false,});
        await signOut(auth);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <button className="login-close-btn" onClick={() => navigate('/')} style={{ visibility: currentUser ? 'visible' : 'hidden' }}>
        <FaTimes />
      </button>

      <form className="login-form">
        <div className="login-header-spacer" />
        <div className="login-form-grid">
          <div className="login-form-group">
            <label className="login-label">Email</label>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleEmailBlur}
              className="login-input-field"
              required
            />
          </div>

          <div className="login-form-group">
            <label className="login-label">Security Code</label>
            <input
              type="text"
              name="securityCode"
              placeholder="Enter security code"
              value={securityCode}
              onChange={(e) => setSecurityCode(e.target.value)}
              className="login-input-field"
              required
            />
          </div>

          {isFirstLogin && (
            <div className="login-form-group">
              <label className="login-label">Reference Code{isPreApprovedLogin ? ' (from your invite)' : ''}</label>
              <input
                type="text"
                name="referenceCode"
                placeholder="Enter reference code from support"
                value={referenceCode}
                onChange={(e) => setReferenceCode(e.target.value)}
                className="login-input-field"
                disabled={isPreApprovedLogin}
                readOnly={isPreApprovedLogin}
                autoComplete="off"
                required
              />
            </div>
          )}
        </div>

        {error && <div className="login-error">
          {isCheckingProfile && <div className="spinner"></div>}
          {error}
        </div>}

        <button 
          type="button" 
          className="login-submit-btn" 
          onClick={handleLogin}
          disabled={!email || !securityCode || (isFirstLogin && !referenceCode) || isSubmitting}
        >
          {isSubmitting ? 'Logging In...' : 'Login'}
        </button>

        {isSubmitting && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </form>

      <div className="login-signup-link-wrapper">
        <a href="/signup" className="login-signup-link" onMouseDown={() => { navigatingToSignup.current = true; }}>
          New user? Sign Up <FaArrowRight className="login-arrow-icon" />
        </a>
      </div>
      <style>{`
        .login-container {
          max-width: 600px;
          margin: 2rem auto;
          padding: 2rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          position: relative;
          font-size: 1.1rem;
        }
        .login-close-btn {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          cursor: pointer;
          color: #666;
          font-size: 1.3rem;
          z-index: 1;
        }
        .login-header-spacer {
          height: 2rem;
        }
        .login-form-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .login-form-group {
          margin-bottom: 1rem;
        }
        .login-label {
          display: block;
          margin-bottom: 0.3rem;
          color: #555;
          font-size: 1.15rem;
        }
        .login-container .login-input-field {
          width: 100%;
          padding: 0.6rem 0;
          border: none !important;
          border-bottom: 1px solid #ddd !important;
          font-size: 1.15rem !important;
          background: transparent;
          color: #333;
        }
        .login-submit-btn {
          width: 100%;
          padding: 0.85rem;
          background: #cccccc;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 0.85rem;
          cursor: pointer;
          margin-top: 1.5rem;
        }
        .login-submit-btn:not(:disabled) {
          background: #fa4848;
        }
        .login-submit-btn:disabled {
          background: #cccccc !important;
          color: #fff !important;
          cursor: not-allowed;
        }
        html[data-theme="dark"] .login-submit-btn:disabled {
          background: #555 !important;
          color: #999 !important;
        }
        .login-signup-link-wrapper {
          text-align: center;
          margin-top: 1.2rem;
        }
        .login-signup-link {
          color: #fa4848;
          font-size: 1rem;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .login-arrow-icon {
          font-size: 12px;
        }
        .login-error {
          color: #fa4848;
          font-size: 0.95rem;
          margin: 1rem 0;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          flex-direction: column;
        }
        .spinner {
          border: 4px solid rgba(0, 0, 0, 0.1);
          border-left-color: #fa4848;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 600px) {
          .login-container {
            padding: 1.5rem;
            margin: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;